import React, {useEffect, useMemo, useState, useCallback} from "react";
import {inject, observer} from "mobx-react";
import {useNavigate, useLocation, Navigate} from "react-router-dom";

//local
import {
  MAIN_PAGE,
  JOINING_ORG,
  ONBOARDING,
  PROJECTS,
  PUBLIC_RFTS,
  MAIN_SETTINGS,
  INVITE_COMPANIES,
  INVITE_ONBOARDING,
  RFP_OFFERS,
  PUBLIC_SIGN_CONTRACT,
  ID_IDENTIFY,
  ORGANIZATIONS,
  SAVE_SEARCH_REGIONS,
  SAVE_SEARCH_SERVICE_CATEGORIES,
  SAVE_SEARCH_SERVICE_CATEGORIES_AND_REGIONS,
  ORGANIZATION_USER_INVITE,
  ORGANIZATION_USER_INVITE_CHOICE,
} from "constants/routes.const";
import {hasPermission} from "helpers/helper";
import {Preloader} from "components/Preloader";
import OnboardingPage from "pages/OnboardingPage/OnboardingPage";
import RFPOffersPage from "pages/Public/RFPOffers/RFPOffersPage";
import SignContractPage from "pages/SignContracts/SignContractPage";
import Modals from "components/modals";
import {pathPermissions} from "constants/user.consts";
import {contractModalNames} from "constants/modal.conts";

export const PrivateRoute = inject("store")(
  observer(
    ({
      store: {auth, organization, seller, clientStore, publicStore},
      component: Component,
      path,
    }) => {
      const {
        joinRequest,
        organizationId,
        token,
        phoneNumber,
        loading,
        modalName,
        subscribedPermissions,
        activeOrganizationId,
        hasActiveOrganization,
        organizationSubscription,
        isApplicant,
        isJoinDenied,
        needsCompany,
      } = auth;

      const navigate = useNavigate();
      const location = useLocation();

      // hash for substitution, if necessary, a direct link to any settings tab
      const [currentHash] = useState(location.hash);

      const {isBankId, isOrgSettings, markings} = organization;
      const {commonSettings} = publicStore;

      // an applicant has no organization data to load either
      const isOnboardingFlow = useMemo(
        () =>
          isApplicant ||
          [
            ONBOARDING,
            ID_IDENTIFY,
            INVITE_ONBOARDING,
            SAVE_SEARCH_REGIONS,
            SAVE_SEARCH_SERVICE_CATEGORIES,
            SAVE_SEARCH_SERVICE_CATEGORIES_AND_REGIONS,
            JOINING_ORG,
            ORGANIZATION_USER_INVITE,
            ORGANIZATION_USER_INVITE_CHOICE,
          ].includes(path),
        [path, isApplicant]
      );

      // is need read the statuses of mandatory one-time redirects
      const isNeedStatus = useMemo(
        () =>
          path !== RFP_OFFERS &&
          path !== ID_IDENTIFY &&
          path !== SAVE_SEARCH_REGIONS &&
          path !== SAVE_SEARCH_SERVICE_CATEGORIES &&
          hasActiveOrganization &&
          (!isBankId || !isOrgSettings) &&
          !joinRequest?.length,
        [hasActiveOrganization, isBankId, isOrgSettings, joinRequest, path]
      );

      const hasNoPermission = useMemo(
        () => (pathPermissions[path] ? !hasPermission([pathPermissions[path]]) : false),
        [path]
      );

      const isNeedOnboarding = !organizationId && !joinRequest?.length && !!phoneNumber;
      const noNeedOnboarding = hasActiveOrganization && path === ONBOARDING;
      const hasTokenAfterJoining =
        !joinRequest?.length && organizationId && path === JOINING_ORG;
      const defaultBuyerPath = hasPermission([pathPermissions[PROJECTS]])
        ? PROJECTS
        : ORGANIZATIONS;
      const defaultSellerPath = hasPermission([pathPermissions[PUBLIC_RFTS]])
        ? PUBLIC_RFTS
        : ORGANIZATIONS;
      const isPublicRoute =
        path.includes(RFP_OFFERS) || path.includes(PUBLIC_SIGN_CONTRACT);
      const isUnAuthorizedZone = !path.includes(JOINING_ORG) && !isPublicRoute;

      const handleCloseModal = () => {
        auth.modalName === contractModalNames.identifyContractPerson &&
          auth.getBankIdCancelOnContract();
        auth.updateAuthModalName();
      };

      const handleProvideStatuses = useCallback(
        (data) => {
          organization.setMandatoryStatuses(data);
          path !== ID_IDENTIFY && !data.bankId && navigate(ID_IDENTIFY);
          if (
            isBankId &&
            ![SAVE_SEARCH_REGIONS, SAVE_SEARCH_SERVICE_CATEGORIES].includes(path)
          ) {
            if (!data?.onboardingSettings || !data?.onboardingSettings?.isAvailableIn)
              navigate(SAVE_SEARCH_REGIONS);
            else if (
              !data?.onboardingSettings ||
              !data?.onboardingSettings?.isServiceCategories
            )
              navigate(SAVE_SEARCH_SERVICE_CATEGORIES);
            else navigate(PROJECTS);
          }
          return null;
        },
        [path, organization, isBankId, navigate]
      );

      const handleSetSubscribedPermissions = useCallback(() => {
        const subscribedPermissions = organization.subscriptions
          .map((subscription) =>
            subscription.SubscribeProductPermissions.map((permission) => [
              permission.PermissionId,
              subscription.name,
            ])
          )
          .flat(1)
          .reduce(
            (permissions, permission) => ({
              ...permissions,
              [permission[0]]: permissions[permission[0]]
                ? [...permissions[permission[0]], permission[1]]
                : [permission[1]],
            }),
            {}
          );
        auth.setSubscribedPermissions(subscribedPermissions);
      }, [auth, organization]);

      // getting common settings of the application
      useEffect(() => {
        !isOnboardingFlow && !commonSettings && publicStore.getCommonSettings();
      }, [commonSettings, publicStore, isOnboardingFlow]);

      // getting common info of the application
      useEffect(() => {
        if (!isOnboardingFlow && (token || isUnAuthorizedZone)) {
          seller.getUnreadRequests();
          seller.getUnsignedSellerContracts();
          organization.getBlackList();
        }
      }, [token, seller, organization, isOnboardingFlow, isUnAuthorizedZone]);
      useEffect(() => {
        token && hasActiveOrganization && !isOnboardingFlow && clientStore.getMessages();
      }, [token, hasActiveOrganization, clientStore, isOnboardingFlow]);
      useEffect(() => {
        if (token && hasActiveOrganization && !isOnboardingFlow && !subscribedPermissions)
          organization.getAvailableSubscriptions().then(handleSetSubscribedPermissions);
      }, [
        token,
        hasActiveOrganization,
        isOnboardingFlow,
        handleSetSubscribedPermissions,
        subscribedPermissions,
        organization,
      ]);
      useEffect(() => {
        if (token && hasActiveOrganization && !isOnboardingFlow && !markings)
          organization.getOrganizationMarkings();
      }, [token, hasActiveOrganization, isOnboardingFlow, markings, organization]);

      // we read the statuses of mandatory one-time redirects
      useEffect(() => {
        if (isNeedStatus) {
          organization
            .getOrganizationOnboardingStatus(activeOrganizationId)
            .then(handleProvideStatuses);
        }
      }, [isNeedStatus, organization, activeOrganizationId, handleProvideStatuses]);

      // VISUALIZATION

      if (loading) return <Preloader />;

      if (modalName) return <Modals onClick={handleCloseModal} />;

      if (token) {
        // determining the user's preferred business role when logging into the application
        const userSubscriptions = organizationSubscription?.SubscribeProducts?.map(
          (item) => item.name
        );
        const isBuyer =
          userSubscriptions?.includes("SUBSCRIPTION_NAME_04") ||
          userSubscriptions?.includes("SUBSCRIPTION_NAME_05");

        // redirects are rendered: navigate() during the first render is ignored in a production build,
        // which left the page blank
        if (path === MAIN_SETTINGS)
          return <Navigate to={`${INVITE_COMPANIES}${currentHash}`} replace />;
        if (
          (joinRequest?.length || (isJoinDenied && !isPublicRoute)) &&
          path !== JOINING_ORG
        )
          return <Navigate to={JOINING_ORG} replace />;
        if (needsCompany && !isPublicRoute && path !== ONBOARDING)
          return <Navigate to={ONBOARDING} replace />;
        if (hasNoPermission || noNeedOnboarding || hasTokenAfterJoining)
          return <Navigate to={isBuyer ? defaultBuyerPath : defaultSellerPath} replace />;
        return <Component />;
      } else {
        if (path === RFP_OFFERS) return <RFPOffersPage />;
        if (path === PUBLIC_SIGN_CONTRACT) return <SignContractPage />;
        if (isNeedOnboarding) return <OnboardingPage />;
        return <Navigate to={MAIN_PAGE} replace />;
      }
    }
  )
);
